import { css } from "@compiled/react";
import { defineMessages } from "react-intl";

import { media } from "@atlaskit/primitives";
import Spinner from "@atlaskit/spinner";

import {
    CurrencyISOCode,
    DEFAULT_COUNTRY_ISO_CODE,
    DEFAULT_CURRENCY_ISO_CODE
} from "@/components/wpl/pricing/src/price-finder/constants.tsx";
import { PriceFinderContextProvider } from "@/components/wpl/pricing/src/price-finder-v3";
import { CcpPricingData } from "@/components/wpl/pricing/src/price-finder-v3/common.ts";
import {
    PriceFinderInputs,
    PriceFinderInputsMessages
} from "@/components/wpl/pricing/src/price-finder-v3/components";
import {
    jiraOfferingSkusArray,
    jiraPlanLimitConfigs,
    jiraBillingCycleConfig,
    JIRA_DEFAULT_BILLING_CYCLE,
    JIRA_DEFAULT_UNIT_COUNT,
    JIRA_MIN_ENTERPRISE_USER_COUNT,
    jiraOfferingSkus
} from "@/components/wpl/pricing/src/price-finder-v3/configs";
import {
    useCcpPricingData,
    useSelector
} from "@/components/wpl/pricing/src/price-finder-v3/hooks.ts";
import * as selectors from "@/components/wpl/pricing/src/price-finder-v3/selectors.ts";
import { PricingPageFallback } from "@/components/wpl/pricing/src/pricing-page-layout/pricing-page-fallback.tsx";
import {
    COUNTRY_CODE_TO_CURRENCY,
    useGeoLocation,
    UserLocale
} from "@/utils/useGeoCurrency.ts";

import { PlanEnterprise } from "./plan-enterprise.tsx";
import { PlanFree } from "./plan-free.tsx";
import { PlanPremium } from "./plan-premium.tsx";
import { PlanStandard } from "./plan-standard.tsx";
import { DATA_PRODUCT_KEY } from "./utils/constants";

export interface JiraPriceFinderJfind1384Props {
    ssrCcpPricingData?: CcpPricingData;
    countryCode?: UserLocale;
}

export const JiraPriceFinderJfind1384 = ({
    ssrCcpPricingData,
    countryCode = DEFAULT_COUNTRY_ISO_CODE
}: JiraPriceFinderJfind1384Props) => {
    const { data: ccpPricingData, status } = useCcpPricingData({
        ssrData: ssrCcpPricingData,
        offeringSkus: jiraOfferingSkusArray
    });

    let userCountryCode = countryCode;
    let currencyISOCode = DEFAULT_CURRENCY_ISO_CODE as CurrencyISOCode;
    const { countryCode: fetchedCountryCode, status: locationStatus } =
        useGeoLocation(window["PRODUCT_CATALOG_LOCATION_BASE_URL"] as string);

    if (locationStatus === "complete" && fetchedCountryCode) {
        userCountryCode = fetchedCountryCode;
    }

    currencyISOCode = (COUNTRY_CODE_TO_CURRENCY[userCountryCode] ||
        DEFAULT_CURRENCY_ISO_CODE) as CurrencyISOCode;

    if (status === "pending" || locationStatus === "fetching") {
        return (
            <div
                css={css({
                    height: "500px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                })}
            >
                <Spinner />
            </div>
        );
    }
    if (status === "error") {
        return <PricingPageFallback />;
    }

    const priceFinderInputsMessages = defineMessages({
        unitCountLabel: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitsLabel: {
            id: "JiraSoftwarePricingPage.priceFinder.unitCountInputTerm",
            defaultMessage: "users"
        }
    });

    return (
        <PriceFinderContextProvider
            config={{
                ccpPricingData,
                planLimitConfigs: jiraPlanLimitConfigs,
                unitMinEnterprise: JIRA_MIN_ENTERPRISE_USER_COUNT,
                currencyISOCode: currencyISOCode,
                localeISOCode: "en",
                billingCycleConfig: jiraBillingCycleConfig
            }}
            inputValues={{
                billingCycle: JIRA_DEFAULT_BILLING_CYCLE,
                unitCount: JIRA_DEFAULT_UNIT_COUNT
            }}
        >
            <div
                css={css({
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    width: "100%"
                })}
            >
                <div>
                    <PriceFinderInputs
                        messages={priceFinderInputsMessages}
                        dataProductKey={DATA_PRODUCT_KEY}
                    />
                </div>
                <PriceFinderOutput />
            </div>
        </PriceFinderContextProvider>
    );
};

const PriceFinderOutput = () => {
    const isEnterpriseRecommended = useSelector((ctx) =>
        selectors.isOfferingRecommended(ctx, jiraOfferingSkus.enterprise)
    );

    const standardPlans = (
        <div
            css={css({
                display: "flex",
                width: "100%",
                [media.below.md]: {
                    flexDirection: "column"
                }
            })}
        >
            <PlanFree />
            <PlanStandard />
            <PlanPremium />
        </div>
    );

    const enterprisePlan = (
        <div>
            <PlanEnterprise />
        </div>
    );

    return (
        <>
            {isEnterpriseRecommended ? (
                <>
                    {enterprisePlan}
                    {standardPlans}
                </>
            ) : (
                <>
                    {standardPlans}
                    {enterprisePlan}
                </>
            )}
        </>
    );
};
